import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { get } from './client';
import { chargersKey } from './chargers';
import { dashboardKey, sessionsKey } from './sessions';
import { reportsKey } from './reports';
import { payToChargeKey } from './payToCharge';

/**
 * Keeps one live connection to /api/live while signed in, and refreshes charger and session data on
 * screen when the OCPP service reports a change (status, session, command answer).
 * Several events in a burst cause one refresh. If the connection drops (the server
 * renews it every 10 minutes, or the sign-in token expired), it checks the sign-in
 * (renewing it) and reconnects.
 */
export function useLiveUpdates(enabled: boolean) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return;
    let source: EventSource | null = null;
    let stopped = false;
    let pending: number | undefined;
    let slow: number | undefined;
    let retry: number | undefined;

    const refresh = () => {
      window.clearTimeout(pending);
      pending = window.setTimeout(() => void qc.invalidateQueries({ queryKey: chargersKey }), 300);
      // Session lists and Home's totals: at most every 5 seconds, however busy the chargers are.
      slow ??= window.setTimeout(() => {
        slow = undefined;
        void qc.invalidateQueries({ queryKey: sessionsKey });
        void qc.invalidateQueries({ queryKey: dashboardKey });
        void qc.invalidateQueries({ queryKey: reportsKey });
        void qc.invalidateQueries({ queryKey: payToChargeKey });
      }, 5000);
    };

    const open = () => {
      if (stopped) return;
      source = new EventSource('/api/live');
      source.addEventListener('ocpp', refresh);
      source.onopen = refresh; // catch anything missed while disconnected
      source.onerror = () => {
        source?.close();
        source = null;
        if (stopped) return;
        // Renew the sign-in if needed (any API call does), then reconnect.
        retry = window.setTimeout(() => {
          get('/auth/me').then(open, () => (retry = window.setTimeout(open, 15_000)));
        }, 3000);
      };
    };
    open();

    return () => {
      stopped = true;
      window.clearTimeout(pending);
      window.clearTimeout(slow);
      window.clearTimeout(retry);
      source?.close();
    };
  }, [enabled, qc]);
}
